import React from 'react';

interface FeatureCardProps {
  icon: React.ReactNode;
  title: string;
  description: string;
  isComingSoon?: boolean;
  onClick?: () => void;
}

const FeatureCard: React.FC<FeatureCardProps> = ({ icon, title, description, onClick }) => {
  return (
    <button
      type='button'
      onClick={onClick}
      className='relative w-full bg-gray-50 border border-gray-200 rounded-xl p-4 sm:p-6 flex items-start gap-3 sm:gap-4 text-left transition-all duration-200 hover:bg-gray-100 hover:shadow-md cursor-pointer group'
    >
      {/* Icon with artistic background */}
      <div className='flex-shrink-0 relative'>
        {/* Icon container with soft gradient and texture */}
        <div className='relative w-12 h-12 sm:w-14 sm:h-14 flex items-center justify-center text-xl sm:text-2xl rounded-full overflow-hidden'>
          {/* Multi-layer gradient background */}
          <div className='absolute inset-0 bg-gradient-to-br from-indigo-100 via-violet-100 to-rose-100 group-hover:from-indigo-200 group-hover:via-violet-200 group-hover:to-rose-200 transition-all duration-500'></div>
          <div className='absolute inset-0 bg-gradient-to-tr from-amber-50/40 via-transparent to-blue-50/40'></div>

          {/* Dot pattern texture */}
          <div
            className='absolute inset-0 opacity-40'
            style={{
              backgroundImage: `radial-gradient(circle, currentColor 1px, transparent 1px)`,
              backgroundSize: '4px 4px',
              color: 'rgba(99, 102, 241, 0.3)',
            }}
          ></div>

          {/* Soft radial gradient vignette */}
          <div className='absolute inset-0 bg-[radial-gradient(circle_at_30%_30%,rgba(255,255,255,0.8)_0%,rgba(255,255,255,0)_50%)]'></div>

          {/* Animated inner glow ring */}
          <div className='absolute inset-0 rounded-full ring-2 ring-white/40 group-hover:ring-white/60 transition-all duration-300'></div>

          {/* Icon content with backdrop blur */}
          <div className='relative z-10 flex items-center justify-center w-full h-full backdrop-blur-[2px]'>
            {icon}
          </div>
        </div>

        {/* Decorative glow effect on hover */}
        <div className='absolute inset-0 rounded-full bg-gradient-to-br from-violet-400/20 to-rose-400/20 blur-lg opacity-0 group-hover:opacity-100 transition-opacity duration-500 -z-10'></div>
      </div>

      {/* Content */}
      <div className='flex-1 min-w-0'>
        <h3 className='text-base sm:text-lg font-semibold text-[#202224] mb-1 sm:mb-2'>{title}</h3>
        <p className='text-xs sm:text-sm text-gray-500 leading-relaxed'>{description}</p>
      </div>
    </button>
  );
};

export default FeatureCard;
